/**
 * Secure color utilities to replace ac-colors
 * Uses colorspace library which has minimal dependencies and good security
 */

// Import colorspace with type assertion to handle missing types
const colorspace = require('colorspace') as (input: string) => string;

/**
 * Generate a consistent color from a string (replacement for ac-colors randomFromString)
 * @param input - String to generate color from
 * @returns Hex color string
 */
export const randomFromString = (input: string): string => {
  // Use colorspace to generate consistent color from string
  return colorspace(input);
};

/**
 * Simple color blending function (replacement for ac-colors blend)
 * Blends two hex colors by averaging their RGB values
 * @param color1 - First hex color (e.g., "#ff0000")
 * @param color2 - Second hex color (e.g., "#0000ff")  
 * @param ratio - Blend ratio (0-1, where 0 = color1, 1 = color2, 0.5 = equal blend)
 * @returns Blended hex color
 */
export const blend = (color1: string, color2: string, ratio: number = 0.5): string => {
  // Ensure ratio is between 0 and 1
  ratio = Math.max(0, Math.min(1, ratio));
  
  // Parse hex colors to RGB
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');
  
  const r1 = parseInt(hex1.substring(0, 2), 16);
  const g1 = parseInt(hex1.substring(2, 4), 16);
  const b1 = parseInt(hex1.substring(4, 6), 16);
  
  const r2 = parseInt(hex2.substring(0, 2), 16);
  const g2 = parseInt(hex2.substring(2, 4), 16);
  const b2 = parseInt(hex2.substring(4, 6), 16);
  
  // Blend the colors
  const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
  const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
  const b = Math.round(b1 * (1 - ratio) + b2 * ratio);
  
  // Convert back to hex
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Generate a lighter version of a color (utility function)
 * @param color - Hex color string
 * @param amount - Amount to lighten (0-1)
 * @returns Lightened hex color
 */
export const lighten = (color: string, amount: number = 0.2): string => {
  return blend(color, '#ffffff', amount);
};

/**
 * Generate a darker version of a color (utility function)
 * @param color - Hex color string
 * @param amount - Amount to darken (0-1)
 * @returns Darkened hex color
 */
export const darken = (color: string, amount: number = 0.2): string => {
  return blend(color, '#000000', amount);
};